import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  Button,
  Table,
  Badge,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import axios from "axios";
import moment from "moment";

const LoginAttemptLogs = () => {
  const url = `${process.env.REACT_APP_BASE_URL}`;

  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    locked: 0,
    withAttempts: 0,
    clean: 0,
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    skip: 0,
    per_page: 25,
    sorton: "lastLoginAttempt",
    sortdir: "desc",
  });

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    action: null,
    userId: null,
  });

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${url}/api/admin/login-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Note: axios interceptor unwraps response.data
      if (res.success) {
        setStats(res.data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  // Fetch login logs
  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${url}/api/admin/login-attempts`,
        {
          ...pagination,
          match: searchTerm,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Note: axios interceptor unwraps response.data
      if (res.success) {
        setLogs(res.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
      alert("Failed to fetch login logs");
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchStats();
    fetchLogs();
  }, [pagination, searchTerm]);

  // Unlock account
  const handleUnlock = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${url}/api/admin/unlock-account`,
        { userId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.success) {
        alert("Account unlocked successfully");
        fetchLogs();
        fetchStats();
      } else {
        alert(res.msg || "Failed to unlock account");
      }
    } catch (error) {
      alert(error.message || "Error unlocking account");
    }
    setConfirmModal({ isOpen: false });
  };

  // Reset attempts
  const handleResetAttempts = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${url}/api/admin/reset-attempts`,
        { userId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.success) {
        alert("Login attempts reset successfully");
        fetchLogs();
        fetchStats();
      } else {
        alert(res.msg || "Failed to reset attempts");
      }
    } catch (error) {
      alert(error.message || "Error resetting attempts");
    }
    setConfirmModal({ isOpen: false });
  };

  // Block user
  const handleBlock = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${url}/api/admin/block-user`,
        { userId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.success) {
        alert("User blocked successfully");
        fetchLogs();
      } else {
        alert(res.msg || "Failed to block user");
      }
    } catch (error) {
      alert(error.message || "Error blocking user");
    }
    setConfirmModal({ isOpen: false });
  };

  // Unblock user
  const handleUnblock = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${url}/api/admin/unblock-user`,
        { userId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.success) {
        alert("User unblocked successfully");
        fetchLogs();
      } else {
        alert(res.msg || "Failed to unblock user");
      }
    } catch (error) {
      alert(error.message || "Error unblocking user");
    }
    setConfirmModal({ isOpen: false });
  };

  // Confirm action modal
  const showConfirmModal = (title, message, action, userId) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      action,
      userId,
    });
  };

  // Execute confirmed action
  const executeAction = () => {
    const { action, userId } = confirmModal;

    switch (action) {
      case "unlock":
        handleUnlock(userId);
        break;
      case "reset":
        handleResetAttempts(userId);
        break;
      case "block":
        handleBlock(userId);
        break;
      case "unblock":
        handleUnblock(userId);
        break;
      default:
        setConfirmModal({ isOpen: false });
    }
  };

  // Format location
  const formatLocation = (log) => {
    const { locationCoordinates } = log;

    if (!locationCoordinates) return "-";

    // GPS coordinates
    if (locationCoordinates.latitude && locationCoordinates.longitude) {
      const lat = locationCoordinates.latitude.toFixed(4);
      const lng = locationCoordinates.longitude.toFixed(4);
      const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;

      return (
        <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
          <i className="ri-map-pin-line me-1"></i>
          {lat}, {lng}
        </a>
      );
    }

    // City/Country
    if (locationCoordinates.city || locationCoordinates.country) {
      return `${locationCoordinates.city || ""}, ${locationCoordinates.country || ""}`;
    }

    return "-";
  };

  // Get status badge
  const getStatusBadge = (log) => {
    if (!log.userId?.active) {
      return <Badge color="dark">🚫 Blocked</Badge>;
    }

    if (log.isLocked) {
      const remaining = moment(log.lockUntil).fromNow();
      return <Badge color="danger">🔒 Locked (until {remaining})</Badge>;
    }

    if (log.attemptCount > 0) {
      return <Badge color="warning">⚠️ {log.attemptCount} attempts</Badge>;
    }

    return <Badge color="success">✅ Active</Badge>;
  };

  // Get attempt badge color
  const getAttemptBadge = (count) => {
    if (count === 0) return <Badge color="success">0/3</Badge>;
    if (count === 1) return <Badge color="warning">1/3</Badge>;
    if (count === 2) return <Badge color="danger">2/3</Badge>;
    return <Badge color="dark">3/3</Badge>;
  };

  document.title = "Login Attempt Logs - VRR OPD";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Login Attempt Logs" pageTitle="Admin" />

          {/* Statistics Cards */}
          <Row>
            <Col xl={3} md={6}>
              <Card className="card-animate">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-uppercase fw-medium text-muted mb-0">
                        Total Accounts
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <h5 className="text-primary fs-14 mb-0">
                        <i className="ri-user-line align-middle"></i>
                      </h5>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                        {stats.total}
                      </h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={3} md={6}>
              <Card className="card-animate">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-uppercase fw-medium text-muted mb-0">
                        With Attempts
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <h5 className="text-warning fs-14 mb-0">
                        <i className="ri-alert-line align-middle"></i>
                      </h5>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                        {stats.withAttempts}
                      </h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={3} md={6}>
              <Card className="card-animate">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-uppercase fw-medium text-muted mb-0">
                        Locked Accounts
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <h5 className="text-danger fs-14 mb-0">
                        <i className="ri-lock-line align-middle"></i>
                      </h5>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                        {stats.locked}
                      </h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={3} md={6}>
              <Card className="card-animate">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-uppercase fw-medium text-muted mb-0">
                        Clean Records
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <h5 className="text-success fs-14 mb-0">
                        <i className="ri-checkbox-circle-line align-middle"></i>
                      </h5>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                        {stats.clean}
                      </h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Logs Table */}
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <Row className="g-4 align-items-center">
                    <Col sm={3}>
                      <h5 className="card-title mb-0">🔐 Login Attempt Logs</h5>
                    </Col>
                    <Col sm={6}>
                      <div className="search-box">
                        <Input
                          type="text"
                          className="form-control"
                          placeholder="Search by email, IP, city, country..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col sm={3}>
                      <div className="d-flex justify-content-end">
                        <Button
                          color="success"
                          onClick={() => {
                            fetchLogs();
                            fetchStats();
                          }}
                        >
                          <i className="ri-refresh-line me-1"></i>
                          Refresh
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </CardHeader>

                <CardBody>
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-3">Loading logs...</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table-hover table-nowrap align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>User</th>
                            <th>Status</th>
                            <th>Attempts</th>
                            <th>Last Attempt</th>
                            <th>Last Login</th>
                            <th>IP Address</th>
                            <th>Location</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {logs.length === 0 ? (
                            <tr>
                              <td colSpan="9" className="text-center py-4">
                                <i className="ri-file-list-line fs-1 text-muted"></i>
                                <p className="mt-3 text-muted">
                                  No login logs found
                                </p>
                              </td>
                            </tr>
                          ) : (
                            logs.map((log, index) => (
                              <tr key={log._id}>
                                <td>{pagination.skip + index + 1}</td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="flex-shrink-0 me-2">
                                      {log.userId?.photo ? (
                                        <img
                                          src={log.userId.photo}
                                          alt=""
                                          className="avatar-xs rounded-circle"
                                        />
                                      ) : (
                                        <div className="avatar-xs">
                                          <div className="avatar-title rounded-circle bg-primary">
                                            {log.userId?.name?.charAt(0) || "?"}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-grow-1">
                                      <h6 className="mb-0">
                                        {log.userId?.name || "Unknown"}
                                      </h6>
                                      <small className="text-muted">
                                        {log.userEmail}
                                      </small>
                                    </div>
                                  </div>
                                </td>
                                <td>{getStatusBadge(log)}</td>
                                <td>{getAttemptBadge(log.attemptCount)}</td>
                                <td>
                                  {log.lastLoginAttempt
                                    ? moment(log.lastLoginAttempt).format(
                                        "DD MMM YYYY, hh:mm A",
                                      )
                                    : "-"}
                                </td>
                                <td>
                                  {log.lastLoggedIn
                                    ? moment(log.lastLoggedIn).format(
                                        "DD MMM YYYY, hh:mm A",
                                      )
                                    : "-"}
                                </td>
                                <td>
                                  <code>{log.ipAddress || "-"}</code>
                                </td>
                                <td>{formatLocation(log)}</td>
                                <td>
                                  <UncontrolledDropdown>
                                    <DropdownToggle
                                      tag="button"
                                      className="btn btn-sm btn-light"
                                    >
                                      <i className="ri-more-2-fill"></i>
                                    </DropdownToggle>
                                    <DropdownMenu end>
                                      {log.isLocked && (
                                        <DropdownItem
                                          onClick={() =>
                                            showConfirmModal(
                                              "Unlock Account",
                                              `Are you sure you want to unlock ${log.userId?.name}'s account?`,
                                              "unlock",
                                              log.userId?._id,
                                            )
                                          }
                                        >
                                          <i className="ri-lock-unlock-line me-2"></i>
                                          Unlock Account
                                        </DropdownItem>
                                      )}
                                      {log.attemptCount > 0 && (
                                        <DropdownItem
                                          onClick={() =>
                                            showConfirmModal(
                                              "Reset Attempts",
                                              `Reset login attempts for ${log.userId?.name}?`,
                                              "reset",
                                              log.userId?._id,
                                            )
                                          }
                                        >
                                          <i className="ri-refresh-line me-2"></i>
                                          Reset Attempts
                                        </DropdownItem>
                                      )}
                                      {log.userId?.active ? (
                                        <DropdownItem
                                          onClick={() =>
                                            showConfirmModal(
                                              "Block User",
                                              `Are you sure you want to block ${log.userId?.name}? They will not be able to login.`,
                                              "block",
                                              log.userId?._id,
                                            )
                                          }
                                          className="text-danger"
                                        >
                                          <i className="ri-forbid-line me-2"></i>
                                          Block User
                                        </DropdownItem>
                                      ) : (
                                        <DropdownItem
                                          onClick={() =>
                                            showConfirmModal(
                                              "Unblock User",
                                              `Unblock ${log.userId?.name}?`,
                                              "unblock",
                                              log.userId?._id,
                                            )
                                          }
                                          className="text-success"
                                        >
                                          <i className="ri-checkbox-circle-line me-2"></i>
                                          Unblock User
                                        </DropdownItem>
                                      )}
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmModal.isOpen}
        toggle={() => setConfirmModal({ isOpen: false })}
        centered
      >
        <ModalHeader toggle={() => setConfirmModal({ isOpen: false })}>
          {confirmModal.title}
        </ModalHeader>
        <ModalBody>
          <p>{confirmModal.message}</p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setConfirmModal({ isOpen: false })}
          >
            Cancel
          </Button>
          <Button color="primary" onClick={executeAction}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default LoginAttemptLogs;
